import React, { useEffect, useState } from 'react';
import {
  MdBarChart, MdFileDownload, MdPictureAsPdf, MdAssignment,
  MdInventory, MdSwapHoriz, MdStorage, MdTune, MdSave, MdAdd, MdDelete,
  MdRefresh,
} from 'react-icons/md';
import api from '../api/axios';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import { confirm, prompt } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

const Toast = ({ msg, type, onClose }) => (
  <div style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 18px', borderRadius: 8, color: 'white',
    background: type === 'error' ? '#d32f2f' : type === 'warning' ? '#f57c00' : '#2e7d32',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>×</button>
  </div>
);

const ENTITY_TABS = [
  { key: 'assets',        label: 'Assets',        icon: <MdInventory />,    title: 'Asset Register' },
  { key: 'movements',     label: 'Movements',     icon: <MdSwapHoriz />,    title: 'Asset Movement History' },
  { key: 'raw_inventory', label: 'Raw Inventory', icon: <MdStorage />,      title: 'Raw Inventory Scan Log' },
  { key: 'audits',        label: 'Audits',        icon: <MdAssignment />,   title: 'Audit History' },
  { key: 'custom',        label: 'Custom',        icon: <MdTune />,         title: 'Custom Report Builder' },
];

const Reports = () => {
  const [tab, setTab] = useState('assets');
  const [catalog, setCatalog] = useState({});
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({});
  const [columns, setColumns] = useState(null);   // null = use defaults
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  // L35: PDF page size + orientation
  const [pdfOpts, setPdfOpts] = useState({ pageSize: 'A4', orientation: 'landscape' });

  const showToast = (m, t = 'success') => { setToast({ msg: m, type: t }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    api.get('/reports/_catalog').then(r => setCatalog(r.data)).catch(() => {});
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try { const r = await api.get('/reports/templates'); setTemplates(r.data); } catch {}
  };

  // Reset filters and columns when tab changes
  useEffect(() => {
    setFilters({});
    setColumns(null);
    setPreview(null);
  }, [tab]);

  const currentEntity = tab === 'custom' ? null : tab;
  const entityMeta = currentEntity ? catalog[currentEntity] : null;

  const allColumns = entityMeta?.columns || [];
  const activeColumnKeys = columns
    || allColumns.filter(c => c.default).map(c => c.key);

  const toggleColumn = (key) => {
    const next = activeColumnKeys.includes(key)
      ? activeColumnKeys.filter(k => k !== key)
      : [...activeColumnKeys, key];
    setColumns(next);
  };

  const handlePreview = async (entityOverride) => {
    const ent = entityOverride || currentEntity;
    if (!ent) return;
    setLoading(true);
    try {
      const r = await api.post('/reports/run', { entity: ent, filters, columns: activeColumnKeys });
      setPreview(r.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Preview failed', 'error');
    } finally { setLoading(false); }
  };

  const handleExport = async (format) => {
    if (!currentEntity) return;
    const params = new URLSearchParams();
    if (activeColumnKeys.length) params.set('columns', activeColumnKeys.join(','));
    for (const [k, v] of Object.entries(filters)) {
      if (v !== '' && v != null) params.set(k, v);
    }
    if (format === 'pdf') {
      params.set('pageSize', pdfOpts.pageSize);
      params.set('orientation', pdfOpts.orientation);
    }
    try {
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = tsFilename(currentEntity, ext);
      await downloadFromApi(`/reports/${currentEntity}/${format}?${params.toString()}`, filename);
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  const handleSaveTemplate = async () => {
    if (!tplName.trim()) { showToast('Template name required', 'error'); return; }
    if (!currentEntity) return;
    try {
      await api.post('/reports/templates', {
        name: tplName.trim(),
        base_entity: currentEntity,
        columns: activeColumnKeys,
        filters,
      });
      setTplName('');
      showToast('Template saved');
      loadTemplates();
    } catch (err) {
      showToast(err.response?.data?.error || 'Save failed', 'error');
    }
  };

  const loadTemplate = (t) => {
    setTab(t.base_entity);
    setColumns(t.columns?.length ? t.columns : null);
    setFilters(t.filters || {});
    setTimeout(() => handlePreview(t.base_entity), 100);
  };

  const handleDeleteTemplate = async (t) => {
    if (!(await confirm(`Delete template "${t.name}"?`))) return;
    try {
      await api.delete(`/reports/templates/${t.id}`);
      showToast('Template deleted');
      loadTemplates();
    } catch (err) { showToast('Delete failed', 'error'); }
  };

  // L36: clone an existing template — useful before editing without losing original
  const handleDuplicateTemplate = async (t) => {
    const newName = await prompt('Name for the duplicate:', `${t.name} (copy)`);
    if (!newName?.trim()) return;
    try {
      await api.post(`/reports/templates/${t.id}/duplicate`, { name: newName.trim() });
      showToast('Template duplicated');
      loadTemplates();
    } catch (err) { showToast(err.response?.data?.error || 'Duplicate failed', 'error'); }
  };

  // ── Filter UI per entity ────────────────────────────────────────────────────
  const renderFilters = () => {
    const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
    if (!currentEntity) return null;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        {(currentEntity === 'assets' || currentEntity === 'movements' || currentEntity === 'audits') && (
          <div>
            <label className="form-label">Location</label>
            <SearchableSelect className="form-control" value={filters.location_id || ''}
              onChange={e => set('location_id', e.target.value || undefined)}>
              <option value="">All</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
            </SearchableSelect>
          </div>
        )}
        {currentEntity === 'assets' && (
          <>
            <div>
              <label className="form-label">Category</label>
              <SearchableSelect className="form-control" value={filters.category || ''}
                onChange={e => set('category', e.target.value || undefined)}>
                <option value="">All</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </SearchableSelect>
            </div>
            <div>
              <label className="form-label">Status</label>
              <SearchableSelect className="form-control" value={filters.status || ''}
                onChange={e => set('status', e.target.value || undefined)}>
                <option value="">All</option>
                {['active','inactive','maintenance','retired','lost','disposed'].map(s =>
                  <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
              </SearchableSelect>
            </div>
          </>
        )}
        {currentEntity === 'movements' && (
          <div>
            <label className="form-label">Status</label>
            <SearchableSelect className="form-control" value={filters.status || ''}
              onChange={e => set('status', e.target.value || undefined)}>
              <option value="">All</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </SearchableSelect>
          </div>
        )}
        {currentEntity === 'audits' && (
          <div>
            <label className="form-label">Status</label>
            <SearchableSelect className="form-control" value={filters.status || ''}
              onChange={e => set('status', e.target.value || undefined)}>
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In progress</option>
              <option value="paused">Paused</option>
            </SearchableSelect>
          </div>
        )}
        {currentEntity === 'raw_inventory' && (
          <div>
            <label className="form-label">Only unknown tags</label>
            <SearchableSelect className="form-control" value={filters.only_unknown || ''}
              onChange={e => set('only_unknown', e.target.value || undefined)}>
              <option value="">No filter</option>
              <option value="true">Unknown tags only</option>
            </SearchableSelect>
          </div>
        )}
        <div>
          <label className="form-label">Start date</label>
          <input type="date" className="form-control" value={filters.start_date || ''}
            onChange={e => set('start_date', e.target.value || undefined)} />
        </div>
        <div>
          <label className="form-label">End date</label>
          <input type="date" className="form-control" value={filters.end_date || ''}
            onChange={e => set('end_date', e.target.value || undefined)} />
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <div className="page-title"><MdBarChart /> Reports</div>
          <div className="page-subtitle">Generate, preview, and export structured reports</div>
        </div>
      </div>

      {/* Entity tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {ENTITY_TABS.map(t => (
          <button key={t.key}
            className={`btn ${tab === t.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'custom' ? (
        <>
          {/* Saved templates */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title"><MdTune style={{ color: 'var(--accent)' }} /> Saved Templates</div>
              <span className="badge badge-info">{templates.length}</span>
            </div>
            {templates.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>
                No saved templates yet. Open any other tab, configure columns + filters, then save it as a template.
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr><th>Name</th><th>Entity</th><th>Columns</th><th>Filters</th><th>Created</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {templates.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontWeight: 500 }}>{t.name}</td>
                        <td>{catalog[t.base_entity]?.title || t.base_entity}</td>
                        <td style={{ fontSize: 12, color: '#666' }}>{t.columns.join(', ') || '(defaults)'}</td>
                        <td style={{ fontSize: 12, color: '#666' }}>{Object.keys(t.filters || {}).filter(k => t.filters[k]).map(k => `${k}=${t.filters[k]}`).join(', ') || 'none'}</td>
                        <td style={{ fontSize: 11, color: '#888' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn-primary btn-sm" onClick={() => loadTemplate(t)}>Open</button>
                            <button className="btn btn-outline btn-sm" onClick={() => handleDuplicateTemplate(t)} title="Duplicate (so editing doesn't overwrite this one)">Duplicate</button>
                            <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDeleteTemplate(t)}><MdDelete /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div style={{ padding: 16, fontSize: 13, color: '#444', lineHeight: 1.6 }}>
              <strong>How custom templates work:</strong> Open any of the tabs above (Assets / Movements / Raw Inventory / Audits),
              pick the columns you want, set filters, then save the configuration as a named template. Templates can also be
              attached to scheduled email reports in <em>Settings → Report Schedules</em>.
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Filters */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">{entityMeta?.title || 'Report'}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline btn-sm" onClick={() => handlePreview()} disabled={loading}>
                  <MdRefresh /> {loading ? 'Loading…' : 'Preview'}
                </button>
                <button className="btn btn-success btn-sm" onClick={() => handleExport('excel')}>
                  <MdFileDownload /> Excel
                </button>
                <SearchableSelect className="form-control form-control-sm" value={pdfOpts.pageSize}
                  onChange={e => setPdfOpts(o => ({ ...o, pageSize: e.target.value }))}
                  title="PDF page size" style={{ height: 32, fontSize: 12, padding: '4px 8px' }}>
                  {['A4', 'LETTER', 'LEGAL', 'A3', 'A5'].map(s => <option key={s} value={s}>{s}</option>)}
                </SearchableSelect>
                <SearchableSelect className="form-control form-control-sm" value={pdfOpts.orientation}
                  onChange={e => setPdfOpts(o => ({ ...o, orientation: e.target.value }))}
                  title="PDF orientation" style={{ height: 32, fontSize: 12, padding: '4px 8px' }}>
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </SearchableSelect>
                <button className="btn btn-danger btn-sm" onClick={() => handleExport('pdf')}>
                  <MdPictureAsPdf /> PDF
                </button>
              </div>
            </div>
            {renderFilters()}
          </div>

          {/* Column picker */}
          {entityMeta && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title" style={{ fontSize: 13 }}>Columns ({activeColumnKeys.length} of {allColumns.length})</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setColumns(allColumns.map(c => c.key))}>All</button>
                  <button className="btn btn-outline btn-sm" onClick={() => setColumns(allColumns.filter(c => c.default).map(c => c.key))}>Default</button>
                  <button className="btn btn-outline btn-sm" onClick={() => setColumns([])}>None</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4, padding: '8px 16px 16px' }}>
                {allColumns.map(c => (
                  <label key={c.key} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                    cursor: 'pointer', borderRadius: 4, fontSize: 12,
                    background: activeColumnKeys.includes(c.key) ? '#e8edf5' : 'transparent',
                  }}>
                    <input type="checkbox" checked={activeColumnKeys.includes(c.key)}
                      onChange={() => toggleColumn(c.key)} />
                    {c.header}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Save as template */}
          {entityMeta && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text" placeholder="Save current settings as template…"
                  value={tplName}
                  onChange={e => setTplName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveTemplate()}
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
                />
                <button className="btn btn-primary btn-sm" onClick={handleSaveTemplate} disabled={!tplName.trim()}>
                  <MdSave /> Save Template
                </button>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Preview ({preview.count} rows)</div>
                {preview.filterSummary && <span style={{ fontSize: 12, color: '#666' }}>{preview.filterSummary}</span>}
              </div>
              {preview.rows.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No matching records</div>
              ) : (
                <div className="table-container" style={{ maxHeight: 500, overflowY: 'auto' }}>
                  <table>
                    <thead>
                      <tr>{preview.columns.map(c => <th key={c.key}>{c.header}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.rows.slice(0, 100).map((row, i) => (
                        <tr key={i}>
                          {preview.columns.map(c => {
                            let v = row[c.key];
                            if (v == null) v = '—';
                            else if (c.key.endsWith('_at') || c.key.endsWith('_date') || c.key === 'timestamp') {
                              try { v = new Date(v).toLocaleString(); } catch {}
                            }
                            return <td key={c.key} style={{ fontSize: 12 }}>{String(v)}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview.rows.length > 100 && (
                <div style={{ padding: '8px 16px', fontSize: 12, color: '#666', textAlign: 'center' }}>
                  Showing first 100 of {preview.count} rows. Use Excel/PDF for full export.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
